import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Search, Check } from 'lucide-react';
import { call } from './TodayBoard';

// FROG WEEK - one frog per day, planned ahead, across the top of To-Do > Greg.
//
// Greg, 2026-11-03: "I get paralyzed sometimes. I just have so many important things, and then I
// get a bunch of unimportant things. It's easier to do the unimportant ones... to be able to set
// one frog for each day and plan the week would be really helpful." Weekends are optional ("I can
// drag something in if I want to, but I don't have to"), so an empty Saturday or Sunday is never
// counted as missing.
//
// ⚠️ A MISSED FROG ASKS, IT NEVER ROLLS. Today / Another day / Drop it. today-board.js has the rule.
// ⚠️ Today's frog also leads "Everything for today" below, with EAT THIS FIRST - same frog_picks row.

const GREEN = '#38A169';
const DARK_GREEN = '#276749';
const dayName = (ymd, long) => new Date(ymd + 'T12:00:00').toLocaleDateString([], long
  ? { weekday: 'long', month: 'short', day: 'numeric' } : { weekday: 'short', month: 'short', day: 'numeric' });
const small = { border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted, #59707E)', display: 'inline-flex' };
const pill = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8, fontSize: 12.5,
  fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', background: bg, color, border: '1px solid ' + (border || bg),
});

export default function FrogWeek({ refreshKey = 0, onChanged }) {
  const [w, setW] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(null);     // the date being picked for
  const [q, setQ] = useState('');
  const [dragFrom, setDragFrom] = useState(null);   // a frog's date while it is dragged
  const [hot, setHot] = useState(null);
  const [later, setLater] = useState(null);         // a missed frog's date, choosing another day

  const load = useCallback(() => call('frogs').then(setW).catch(e => setErr(e.message)), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (action, params) => {
    setBusy(true); setErr(null);
    try { setW(await call(action, params)); if (onChanged) onChanged(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  // Ticking it off is the board's own toggle (closes the task, stamps the frog), which answers with
  // the board, not the week - so the week is re-read after.
  const eat = async (f) => {
    setBusy(true); setErr(null);
    try { await call('toggle', { srcTable: f.srcTable, srcId: f.srcId, done: true }); await load(); if (onChanged) onChanged(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const choose = (date, x) => { setPicking(null); setQ(''); act('frog_set', Object.assign({ date }, x)); };

  if (!w) return err ? <div style={{ color: 'var(--orange)', fontSize: 13, marginBottom: 12 }}>Frog week: {err}</div> : null;

  // A frog dragged to another day trades places; a task dragged in from the list below becomes that day's frog.
  const onDrop = (date) => (e) => {
    e.preventDefault(); setHot(null);
    if (dragFrom) { const from = dragFrom; setDragFrom(null); if (from !== date) act('frog_move', { from, to: date }); return; }
    try {
      const x = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (x && x.srcTable && x.srcId) act('frog_set', { date, srcTable: x.srcTable, srcId: x.srcId });
    } catch (_) { /* not a task */ }
  };

  const term = q.trim().toLowerCase();
  const matches = term ? w.pool.filter(p => p.title.toLowerCase().includes(term)).slice(0, 8) : [];

  return (
    <div style={{ border: '1px solid #CFE8D6', background: 'linear-gradient(180deg,#F7FCF8 0%,#fff 100%)', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: DARK_GREEN }}>🐸 Frog week</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted, #59707E)' }}>
          One frog a day, the thing you'd rather put off. Eat it first.
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: DARK_GREEN }}>
          {w.planned > 0 && <span>{w.eaten} of {w.planned} eaten this week</span>}
          {w.emptyWeekdays > 0 && <span style={{ color: 'var(--orange)', marginLeft: 10 }}>{w.emptyWeekdays} weekday{w.emptyWeekdays > 1 ? 's' : ''} without a frog</span>}
        </div>
      </div>

      {err && <div style={{ color: 'var(--orange)', fontSize: 13, marginBottom: 8 }}>{err}</div>}

      {/* Missed frogs: decide, don't drift. */}
      {w.missed.map(m => (
        <div key={m.date} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 12px', marginBottom: 8, borderRadius: 10, background: '#FFF3ED', border: '1px solid #F6C9B4', fontSize: 13 }}>
          <span><b>{dayName(m.date, true)}'s frog wasn't eaten:</b> {m.title}</span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            <button disabled={busy} onClick={() => act('frog_resolve', { date: m.date, to: 'today' })} style={pill('var(--orange)', '#fff')}>Today</button>
            {later === m.date
              ? w.tiles.filter(t => !t.today && !t.frog).map(t => (
                  <button key={t.date} disabled={busy} onClick={() => { setLater(null); act('frog_resolve', { date: m.date, to: t.date }); }}
                    style={pill('#fff', 'var(--navy)', 'var(--line, #D9E2E8)')}>{dayName(t.date).split(',')[0]}</button>))
              : <button disabled={busy} onClick={() => setLater(m.date)} style={pill('#fff', 'var(--navy)', 'var(--line, #D9E2E8)')}>Another day</button>}
            <button disabled={busy} onClick={() => act('frog_resolve', { date: m.date, to: 'drop' })} style={pill('#fff', 'var(--muted, #59707E)', 'var(--line, #D9E2E8)')}>Drop it</button>
          </span>
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        {w.tiles.map(t => {
          const f = t.frog;
          const isHot = hot === t.date;
          return (
            <div key={t.date}
              onDragOver={(e) => { e.preventDefault(); if (hot !== t.date) setHot(t.date); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHot(null); }}
              onDrop={onDrop(t.date)}
              style={{
                minHeight: 92, borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6,
                background: isHot ? '#E6F5EA' : f && f.done ? '#E6F4EA' : f ? '#fff' : t.optional ? 'transparent' : '#fff',
                border: isHot ? '2px dashed ' + GREEN : f ? '2px solid ' + (f.done ? '#B7E1C4' : GREEN)
                  : t.optional ? '1px dashed var(--line, #D9E2E8)' : '1px dashed #9FD3AE',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.today ? DARK_GREEN : 'var(--navy)' }}>
                {t.today ? 'Today' : dayName(t.date).split(',')[0]}
                {!t.today && <span style={{ fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'var(--muted, #59707E)' }}>{dayName(t.date).split(', ')[1]}</span>}
                {t.optional && !f && <span style={{ fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'var(--muted, #59707E)' }}>· optional</span>}
                {f && !f.done && <button title="Clear this day's frog" disabled={busy} onClick={() => act('frog_clear', { date: t.date })} style={{ ...small, marginLeft: 'auto' }}><X size={13} /></button>}
              </div>
              {f ? (
                <div draggable={!f.done} onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', 'frog'); setDragFrom(t.date); }}
                  onDragEnd={() => { setDragFrom(null); setHot(null); }}
                  title={f.done ? 'Eaten' : 'Drag to another day to move it'}
                  style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3, color: f.done ? DARK_GREEN : 'var(--navy)', cursor: f.done ? 'default' : 'grab', textDecoration: f.done ? 'line-through' : 'none' }}>
                  {f.done ? <Check size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> : '🐸 '}{f.title}
                </div>
              ) : (
                <button disabled={busy} onClick={() => { setPicking(picking === t.date ? null : t.date); setQ(''); }}
                  style={{ ...pill('transparent', t.optional ? 'var(--muted, #59707E)' : DARK_GREEN), border: 'none', padding: '4px 0', justifyContent: 'flex-start' }}>
                  <Plus size={14} /> Pick a frog
                </button>
              )}
              {f && t.today && !f.done && (
                <button disabled={busy} onClick={() => eat(f)}
                  style={{ ...pill(GREEN, '#fff'), marginTop: 'auto', alignSelf: 'flex-start' }}><Check size={13} /> Eaten</button>
              )}
            </div>
          );
        })}
      </div>

      {/* The picker: suggestions first (what the ranking would choose, and why), or search everything open. */}
      {picking && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: '#fff', border: '1px solid var(--line, #D9E2E8)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <b style={{ fontSize: 13, color: 'var(--navy)' }}>Frog for {dayName(picking, true)}</b>
            <button onClick={() => setPicking(null)} style={{ ...small, marginLeft: 'auto' }} title="Close"><X size={15} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--line, #D9E2E8)', borderRadius: 8, padding: '0 10px', marginBottom: 8 }}>
            <Search size={14} color="#59707E" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search your tasks, or type a new one"
              onKeyDown={e => { if (e.key === 'Enter' && q.trim()) choose(picking, matches[0] ? { srcTable: matches[0].srcTable, srcId: matches[0].srcId } : { text: q.trim() }); }}
              style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 0', fontSize: 14, fontFamily: 'inherit', background: 'transparent' }} />
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            {!term && w.suggestions.length > 0 && <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted, #59707E)' }}>Suggested</div>}
            {(term ? matches : w.suggestions).map(p => (
              <button key={p.srcTable + p.srcId} disabled={busy} onClick={() => choose(picking, { srcTable: p.srcTable, srcId: p.srcId })}
                style={{ textAlign: 'left', padding: '7px 10px', borderRadius: 8, border: '1px solid transparent', background: '#F7F9FB', cursor: 'pointer', fontFamily: 'inherit' }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy)' }}>{p.title}</div>
                {!term && p.reason && <div style={{ fontSize: 12, color: 'var(--muted, #59707E)', marginTop: 2 }}>{p.reason}</div>}
              </button>
            ))}
            {term && (
              <button disabled={busy} onClick={() => choose(picking, { text: q.trim() })}
                style={{ textAlign: 'left', padding: '7px 10px', borderRadius: 8, border: '1px dashed #9FD3AE', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, color: DARK_GREEN, fontWeight: 600 }}>
                <Plus size={13} style={{ verticalAlign: '-2px' }} /> New task: "{q.trim()}"
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
